import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { useMapPanelContext } from '../MapPanelContext';

interface ResizableTableProps {
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

export const ResizableTable: React.FC<ResizableTableProps> = ({
  className,
  style,
  children,
}) => {
  const { setTableHeight } = useMapPanelContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const childrenRef = useRef<HTMLDivElement>(null);
  const lastHeightRef = useRef<number>(0);
  // Tracks the table panel's pixel height so the shadow-caster div can match it
  const [panelHeight, setPanelHeight] = useState(0);

  // Handle manual resize via the resize handle
  const handleResize = useCallback(
    (sizes: number[]) => {
      // Get the container's total height
      const containerHeight = containerRef.current?.clientHeight || 0;

      // Calculate the actual pixel height of the second panel
      const actualHeight = (sizes[1] / 100) * containerHeight;
      setTableHeight(actualHeight);
      setPanelHeight(actualHeight);
    },
    [setTableHeight],
  );

  useEffect(() => {
    // Set initial height
    const containerHeight = containerRef.current?.clientHeight || 0;
    const actualHeight = 0.5 * containerHeight;
    setTableHeight(actualHeight);
    setPanelHeight(actualHeight);
  }, [setTableHeight]);

  // Periodically check for container height changes
  useEffect(() => {
    // Initial height setup
    if (containerRef.current) {
      lastHeightRef.current = containerRef.current.clientHeight;
    }

    // Set up periodic check
    const intervalId = setInterval(() => {
      if (containerRef.current) {
        const currentHeight = containerRef.current.clientHeight;

        // If height has changed significantly
        if (currentHeight === lastHeightRef.current) {
          return;
        }

        if (!childrenRef.current) {
          return;
        }

        lastHeightRef.current = currentHeight;

        const childrenHeight = childrenRef.current.clientHeight;
        setTableHeight(childrenHeight);
        setPanelHeight(childrenHeight);
      }
    }, 250);

    return () => clearInterval(intervalId);
  }, [setTableHeight]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative h-[calc(100%-218px)] w-full pointer-events-none animate-in fade-in slide-in-from-bottom-3 duration-200',
        className,
      )}
      style={style}
    >
      {/*
        Shadow caster — an invisible div positioned to match the table panel's
        area. We use this because react-resizable-panels applies overflow:hidden
        internally, which clips any box-shadow on children. By placing a sibling
        div outside that context with the same dimensions, we get a proper
        shadow-md, matching the docked side panels. The table is a large docked
        working surface, so it shares the side panels' elevation (shadow-md)
        rather than the smaller floating map cards' (shadow-sm) — elevation is
        keyed to surface type, not to the element's height or screen position,
        so a shorter table still reads at the same level as the taller panel.
        Shared by the report, targets-on-map and routes tables; the popout
        variants render in their own windows and are unaffected.
      */}
      <div
        className="absolute bottom-0 left-0 right-0 shadow-md rounded-lg pointer-events-none"
        style={{ height: panelHeight }}
        aria-hidden="true"
      />

      <ResizablePanelGroup
        direction="vertical"
        onLayout={handleResize}
        className={'flex flex-col h-full w-full border-0 pointer-events-none'}
      >
        <ResizablePanel
          className="flex-1 pointer-events-none"
          style={{ background: 'transparent' }}
        />

        <ResizableHandle
          withHandle
          className="mt-2 bg-transparent pointer-events-auto"
        />

        <ResizablePanel
          className="pointer-events-auto"
          defaultSize={50}
          minSize={25}
        >
          <div
            ref={childrenRef}
            className="w-full h-full rounded-lg overflow-auto"
          >
            {children}
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
};

ResizableTable.displayName = 'ResizableTable';
